import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { DynFormsDaoService } from '../../dao/dyn-form/dyn-forms-dao.service';
import { DynFormsService } from '../dyn-forms.service';
import { DynForm, DynFormDTO } from 'src/app/_model/dyn-form';
import { IMSId, IMSObjectType, IMSObject } from 'src/app/_model/ims-object';
import { DynFormDataDaoService } from '../../dao/dyn-form-data/dyn-form-data-dao.service';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { ClientStateService } from 'src/app/_services/client-state/logic/client-state.service';
import { EmployeeService } from 'src/app/_services/employee/logic/employee.service';
import { CloudSyncState, DynFormData } from 'src/app/_model/dyn-form-data';
import { TourService } from 'src/app/_services/tour/logic/tour.service';
import { TourStopsService } from 'src/app/_services/tour-stop/logic/tour-stop.service';
import { BranchesService } from 'src/app/_services/branches/logic/branches.service';
import { DynFormParserService } from 'src/app/_services/dyn-form-parser/dyn-form-parser.service';
import { AbstractFormField } from 'src/app/_components/dyn-form/form-fields/abstract-form-field/abstract-form-field';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { DatePipe } from '@angular/common';

import { TranslateService } from '@ngx-translate/core';
import { DynFormRemoteActionBuilder } from 'src/app/_model/remote-action/remote-action-builder/dyn-form-remote-action-builder/dyn-form-remoteaction-builder';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { LocationService } from 'src/app/_services/location/location.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { StockService } from 'src/app/_services/stock/logic/stock.service';
import { CargoItemsService } from 'src/app/_services/cargo-item/logic/cargo-item.service';
import { OrderItemsService } from 'src/app/_services/order-item/logic/order-item.service';
import { DynFormStateKeys } from 'src/app/_components/dyn-form/component/dyn-form.component';
import { CargoItemTypesService } from 'src/app/_services/cargo-item-types/logic/cargo-item-types.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { GeneralPostProcessorService } from 'src/app/_services/post-processors/general-post-processor/general-post-processor.service';
import { DeficiencyTicketsService } from 'src/app/_services/deficiency-ticket/logic/deficiency-tickets.service';
import { Pin } from 'src/app/_model/pin';
import { APILevel } from 'src/app/_services/sync/api-level';
import { AbstractFormFieldXML } from 'src/app/_model/dyn-form/form-fields/abstract-form-field-xml';

/**
 * Loads App Parameters
 */
@Injectable()
export class DynFormsLogicService implements DynFormsService {
  // Public Member
  public dynForms$ = new BehaviorSubject<DynForm[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private appService: AppService,
    private credentialsService: CredentialsService,
    private dynFormDaoService: DynFormsDaoService,
    private dynFormDataDaoService: DynFormDataDaoService,
    private clientStateService: ClientStateService,
    private servicejobsService: ServicejobsService,
    private locationService: LocationService,
    private empService: EmployeeService,
    private tourService: TourService,
    private tourStopsService: TourStopsService,
    private branchesService: BranchesService,
    private deficiencyTicketService: DeficiencyTicketsService,
    private dynFormParser: DynFormParserService,
    private stockService: StockService,
    private cargoItemService: CargoItemsService,
    private orderItemService: OrderItemsService,
    private cargoItemTypesService: CargoItemTypesService,
    private datePipe: DatePipe,
    private appParameter: AppParameterService,
    private generalPostProcessorService: GeneralPostProcessorService
  ) {
    this.initialize();
  }

  // Public Functions
  public getDynFormById(id: IMSId): DynForm {
    if (id === undefined) {
      return undefined;
    }

    /**
     * JB20200127: SD-2535 To due caching of the defvalues when parsing the default
     * values we need to make sure we do not return a reference of the
     * dynform but make a copy of it.
     *
     * Easiest way to make a copy of a data container -> JSON to String and then String to JSON
     */
    let dynForm = this.dynForms$.getValue().find((form) => {
      return form.id.imsId == id.imsId;
    });
    // return dynForm == undefined
    //   ? undefined
    //   : JSON.parse(JSON.stringify(dynForm));
    // JB20260306 Copy via ObjectAssign to keep getter intact
    return dynForm == undefined ? undefined : Object.assign(Object.create(Object.getPrototypeOf(dynForm)), JSON.parse(JSON.stringify(dynForm)));
  }

  public getDynFormByExtId(extId: string): DynForm {
    this.logger.log(this.constructor.name, 'getDynFormByExtId', extId);
    if (extId === undefined) {
      return undefined;
    }

    /**
     * JB20200127: SD-2535 To due caching of the defvalues when parsing the default
     * values we need to make sure we do not return a reference of the
     * dynform but make a copy of it.
     *
     * Easiest way to make a copy of a data container -> JSON to String and then String to JSON
     */
    return JSON.parse(
      JSON.stringify(
        this.dynForms$.getValue().find((form) => {
          return form.extid == extId;
        })
      )
    );
  }

  public async updateDynForms(dynFormsDto: DynFormDTO[]): Promise<void> {
    this.logger.log(
      this.constructor.name,
      '- Received new dyn forms from other IMS component'
    );

    try {
      let dynForms: DynForm[] = [];

      for (const dynFormDto of dynFormsDto) {
        console.log(this.constructor.name, 'Received DynFormDto: ', dynFormDto);
        dynForms.push(await DynForm.fromDto(dynFormDto));
      }

      await this.dynFormDaoService.saveDynForms(dynForms);
      await this.dynForms$.next(dynForms);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error parsing DynForms: ',
        error?.message
      );
      throw error;
    }
  }

  public async isFormFinished(
    dynForm: DynForm,
    targetRef: IMSObjectType,
    object?: IMSObject,
    maxAgeDays: number = 0
  ): Promise<boolean> {
    let obj =
      object != null && object.id.objRef === targetRef
        ? object
        : await this.clientStateService.getImsObjectForForm(targetRef);
    if (obj === undefined) {
      throw new Error(
        this.constructor.name +
          '- Could not load Ims Object ' +
          targetRef +
          ' to load dynForm'
      );
    }

    let hasFinishedData =
      await this.dynFormDataDaoService.hasFinishedNotExpiredFormData(
        dynForm,
        obj,
        this.getAdditionalDynFormDataKey(
          targetRef,
          dynForm.form.form.$.additionalDataKey
        ),
        maxAgeDays
      );

    this.logger.log(this.constructor.name, 'isFormFinished', hasFinishedData);
    return hasFinishedData;
  }

  public async isFormNotRequiredOrValid(
    dynForm: DynForm,
    targetRef: IMSObjectType,
    object?: IMSObject
  ): Promise<boolean> {
    let form = dynForm.form;

    let fields = this.dynFormParser.parseFields(
      dynForm,
      object ?? this.clientStateService.getImsObjectForForm(targetRef)
    );

    let formGroup = this.generateFormGroup(fields);

    let formData = await this.getFormData(dynForm, targetRef);

    if (formData.formData && Object.keys(formData.formData).length !== 0) {
      this.logger.log(
        this.constructor.name,
        'Found old formdata and restore it'
      );
      this.restoreFormData(fields, formData.formData);
    } else if (form.form.$.preloadDataTasks) {
      this.logger.warn(
        this.constructor.name,
        'preloaddatatask in background form not supported yet'
      );
    }

    let isRequired = false;
    for (let field of fields) {
      this.logger.log(
        this.constructor.name,
        'isRequiredAsDependentForm',
        field.name,
        field.isRequiredAsDependentForm()
      );
      isRequired = isRequired || field.isRequiredAsDependentForm();
    }

    return formGroup.valid || !isRequired;
  }

  public generateFormGroup(fields: AbstractFormField[]): UntypedFormGroup {
    let group: {
      [key: string]: AbstractControl;
    } = {};

    group = this.createDynFormStateControls(group);

    fields.forEach((field) => {
      for (const chieldControl of field.generateFormControl()) {
        group[chieldControl.key] = chieldControl.value;
      }
    });
    this.logger.log(
      this.constructor.name,
      '- Created Forms Group from DynFormFields'
    );
    return new UntypedFormGroup(group);
  }

  public restoreFormData(fields: AbstractFormField[], formData: any) {
    for (const field of fields) {
      console.log(
        'JBRef need to update field',
        field.name,
        // 'with referenceName',
        // field.referenceName,
        '=',
        formData?.[field.name]
      );
      if (formData?.[field.name]) {
        field.updateValue(formData, true);
      }
      if (field.namePostfixes && field.namePostfixes.length > 0) {
        let hasPostfixedData = false;
        for (let postfix of field.namePostfixes) {
          if (formData[field.name + postfix]) {
            hasPostfixedData = true;
          }
        }
        if (hasPostfixedData) {
          field.updateValue(formData, true);
        }
      }
    }
  }

  public async generateDynFormRemoteAction(
    formDefinition: DynForm,
    targetRef: IMSObjectType,
    formGroup: UntypedFormGroup,
    formData: DynFormData,
    pin: Pin,
    fields: AbstractFormField[],
    screenshotFilePath: string,
    acId: number,
    acukey: string,
    editable: boolean,
    object?: IMSObject,
    isDraft: boolean = false,
    draftEmpId?: number,
    draftMcId?: number,
    draftServiceJobId?: number,
    isReadyForSignature?: boolean,
    checkAcVersionConflict?: boolean,
    isCloudAutosave?: boolean,
  ): Promise<RemoteAction> {
    let finishTs = Date.now();
    var data = formGroup.getRawValue();

    data['finishTstamp'] = this.datePipe.transform(
      finishTs,
      'dd.MM.yyyy HH:mm'
    );
    data['finishRawTstamp'] = finishTs;
    data['isformfinished'] = 'true';
    data['referenceTs'] = formData.referenceTs;

    let shouldAbortDistribution = false;
    let forceUseManualNextNumberRangeValue: string;
    let attachedExtFileIds: number[] = []
    let requiredExtFileExtFileIds: string[] = []
    for (let field of fields) {
      this.logger.log(
        this.constructor.name,
        'shouldAbortDistribution',
        field.name,
        field.shouldAbortDistribution()
      );
      shouldAbortDistribution =
        shouldAbortDistribution || field.shouldAbortDistribution();
      await field.onPreSend();
      if (field.isManualNextNumberRangeValue) {
        forceUseManualNextNumberRangeValue = data[field.name]
      }

      attachedExtFileIds = attachedExtFileIds.concat(field.attachedExtFileIds());
      requiredExtFileExtFileIds = requiredExtFileExtFileIds.concat(field.requiredExtFileExtFileIds());
    }
    formData._formpi_abortdistribution = shouldAbortDistribution;
    if (attachedExtFileIds.length > 0) {
      formData._formpi_attachedextfileids = attachedExtFileIds.join(",")
    }

    if (requiredExtFileExtFileIds.length > 0) {
      formData._formpi_requiredextfileextfileids = requiredExtFileExtFileIds.join(",")
    }

    formData.isFormFinished = true;

    formData.finishTs = finishTs;
    formData.formData = data;

    await this.executeForBinds(data, fields);

    let dfrab = new DynFormRemoteActionBuilder(
      formDefinition,
      object ?? this.clientStateService.getImsObjectForForm(targetRef),
      formData,
      pin,
      this.appService,
      this.appParameter,
      this.credentialsService,
      this.clientStateService,
      this.servicejobsService,
      this.locationService,
      this.stockService,
      this.tourService,
      this.tourStopsService,
      this.cargoItemService,
      this.branchesService,
      this.orderItemService,
      this.cargoItemTypesService,
      formData.formData,
      screenshotFilePath ? '#file:' + screenshotFilePath : undefined,
      acId,
      acukey,
      editable,
      this.generalPostProcessorService,
      forceUseManualNextNumberRangeValue,
      isDraft,
      draftEmpId,
      draftMcId,
      draftServiceJobId,
      isReadyForSignature,
      checkAcVersionConflict,
      isCloudAutosave,
      this.logger
    );

    return await dfrab.generateRemoteAction([]);
  }

  public async getFormData(
    dynForm: DynForm,
    targetObjRef: IMSObjectType,
    object?: IMSObject
  ): Promise<DynFormData> {
    let obj =
      object ?? this.clientStateService.getImsObjectForForm(targetObjRef);
    if (obj === undefined) {
      throw new Error(
        this.constructor.name +
          '- Could not load Ims Object ' +
          targetObjRef +
          ' to load dynForm'
      );
    }

    let origFormData = await this.dynFormDataDaoService.getDynFormData(
      dynForm,
      obj,
      this.getAdditionalDynFormDataKey(
        targetObjRef,
        dynForm.form.form.$.additionalDataKey
      )
    );

    let refFields = dynForm.form.form.field.filter((field) => field.$.isRef || field.$.defValue?.toString().startsWith('#form.'));
    if (refFields.length > 0) {
      console.log('JBRef found refFields', refFields);
      // let refs = refFields.map((field) => field.$.defValue);

      for (const refField of refFields) {
        console.log('JBRef parseRefValue', refField.$.defValue);
        let refObject = await this.parseRefValue(refField, targetObjRef);

        console.log('JBRef received refOBj', refObject);

        if (refObject) {
          if (origFormData.formData == undefined) {
            console.log('JBRef origFormData null create');
            origFormData.formData = {};
          }

          console.log(
            'JBRef update field',
            refObject.key,
            'in origFormData',
            origFormData.formData[refObject.key],
            'to',
            refObject.value
          );
          origFormData.formData[refObject.key] = refObject.value;
        }
      }
    }

    return origFormData;
  }

  private async parseRefValue(
    refField: AbstractFormFieldXML,
    targetObjRef: IMSObjectType
  ): Promise<{
    key: string;
    value: any;
  }> {
    let elem = refField.$.defValue.split('.');
    if (elem.length == 3) {
      let formExtId = elem[1];
      let attribut = elem[2];

      let dynForm = this.getDynFormByExtId(formExtId);
      let dynFormData = await this.getFormData(dynForm, targetObjRef);
      if (dynFormData?.formData) {
        return { key: refField.$.name, value: dynFormData?.formData[attribut] };
      } else {
        return { key: refField.$.name, value: undefined };
      }
    } else if (elem.length == 2) {
      let formExtId = elem[1];
      let dynForm = this.getDynFormByExtId(formExtId);

      return { key: refField.$.name, value: dynForm?.name };
    }

    return undefined;
  }

  public async getNotFinishedDynFormDataFor(
    dynForm: DynForm,
    targetObjRef: IMSObjectType,
    object?: IMSObject
  ): Promise<DynFormData[]> {
    let obj =
      object ?? this.clientStateService.getImsObjectForForm(targetObjRef);
    if (obj === undefined) {
      throw new Error(
        this.constructor.name +
          '- Could not load Ims Object ' +
          targetObjRef +
          ' to load dynForm'
      );
    }
    return this.dynFormDataDaoService.getNotFinishedDynFormData(
      dynForm,
      obj,
      this.getAdditionalDynFormDataKey(
        targetObjRef,
        dynForm.form.form.$.additionalDataKey
      )
    );
  }

  public async getNotFinishedJobSectionDynFormDataFor(
    dynForm: DynForm,
    targetObjRef: IMSObjectType,
    object?: IMSObject
  ): Promise<DynFormData[]> {
    let obj =
      object ?? this.clientStateService.getImsObjectForForm(targetObjRef);
    if (obj === undefined) {
      throw new Error(
        this.constructor.name +
          '- Could not load Ims Object ' +
          targetObjRef +
          ' to load dynForm'
      );
    }
    return this.dynFormDataDaoService.getNotFinishedJobSectionDynFormData(
      dynForm,
      obj
    );
  }

  public async getSpecificDynFormDataFor(
    dynFormId: IMSId,
    obj: IMSObject,
    objRef: IMSObjectType,
    mode: string
  ): Promise<DynFormData> {
    return this.dynFormDataDaoService.getSpecificDynFormDataFor(
      dynFormId,
      obj,
      objRef,
      this.getAdditionalDynFormDataKey(
        objRef,
        this.getDynFormById(dynFormId).form.form.$.additionalDataKey
      ),
      mode
    );
  }

  public async getAllDynFormDatasForObj(
    obj: IMSObject
  ): Promise<DynFormData[]> {
    return this.dynFormDataDaoService.getAllDynFormDatasForObj(obj);
  }

  public async getAmountOfFinishedDynFormForObjRefSince(
    dynFormId: IMSId,
    objRef: IMSObjectType,
    timeStamp: number,
    object?: IMSObject
  ): Promise<number> {
    let obj = object ?? this.clientStateService.getImsObjectForForm(objRef);
    if (obj === undefined) {
      throw new Error(
        this.constructor.name +
          '- Could not load Ims Object ' +
          objRef +
          ' to load dynForm'
      );
    }
    return this.dynFormDataDaoService.getAmountOfFinishedDynFormDatasForObjIdSince(
      dynFormId,
      obj,
      this.getAdditionalDynFormDataKey(
        objRef,
        this.getDynFormById(dynFormId).form.form.$.additionalDataKey
      ),
      timeStamp
    );
  }

  public async saveFormData(
    dynFormData: DynFormData,
    dynForm: DynForm,
    targetObjRef: IMSObjectType,
    object?: IMSObject
  ): Promise<number> {
    try {
      let obj =
        object ?? this.clientStateService.getImsObjectForForm(targetObjRef);
      if (obj === undefined) {
        throw new Error(
          this.constructor.name +
            '- Could not load Ims Object ' +
            targetObjRef +
            ' to save dynForm'
        );
      }
      let insertedId = await this.dynFormDataDaoService.saveDynFormData(
        dynFormData,
        dynForm,
        obj,
        this.getAdditionalDynFormDataKey(
          targetObjRef,
          dynForm.form.form.$.additionalDataKey
        )
      );
      return insertedId;
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Error Saving Form Data:',
        error?.message
      );
      return undefined;
    }
  }

  public async updateDynFormDataACInfo(
    dynFormDataId: number,
    acukey: string,
    acid: number,
    chainRootAcId?: number,
    acStatus?: number,
    acIsLocked?: boolean,
    draftOwnerEmpId?: number,
    draftOwnerMcId?: number,
    draftOwnerServiceJobId?: number,
    cloudSyncState?: CloudSyncState,
  ): Promise<void> {
    try {
      return await this.dynFormDataDaoService.updateACInfo(
        dynFormDataId,
        acukey,
        acid,
        chainRootAcId,
        acStatus,
        acIsLocked,
        draftOwnerEmpId,
        draftOwnerMcId,
        draftOwnerServiceJobId,
        cloudSyncState,
      );
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Error updating acukey:',
        error?.message
      );
    }
  }

  public async hasNotFinishedDynFormDataForDraft(
    acukey: string,
    acid: number,
    chainRootAcId?: number,
  ): Promise<boolean> {
    try {
      return await this.dynFormDataDaoService.hasNotFinishedDynFormDataForDraft(
        acukey,
        acid,
        chainRootAcId,
      );
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Error checking unfinished formdata by acukey/acid:',
        error?.message
      );
      return false;
    }
  }

  public async getAcIdentity(
    formDataId: number,
  ): Promise<{ acukey?: string; acid?: number; chainRootAcId?: number }> {
    try {
      return await this.dynFormDataDaoService.getAcIdentity(formDataId);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Error reading AC identity:',
        error?.message
      );
      return {};
    }
  }

  public async updateIsDirty(formDataId: number, isDirty: boolean): Promise<void> {
    try {
      return await this.dynFormDataDaoService.updateIsDirty(formDataId, isDirty);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Error updating isDirty:',
        error?.message
      );
    }
  }

  public async deleteDraftsForChain(
    chainRootAcId: number | undefined,
    acukey: string | undefined,
  ): Promise<void> {
    try {
      return await this.dynFormDataDaoService.deleteDraftsForChain(
        chainRootAcId,
        acukey,
      );
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Error deleting the drafts of chain:',
        error?.message
      );
    }
  }

  public async deleteNotDirtyDraftsForChain(
    chainRootAcId: number | undefined,
    acukey: string | undefined,
  ): Promise<void> {
    try {
      return await this.dynFormDataDaoService.deleteNotDirtyDraftsForChain(
        chainRootAcId,
        acukey,
      );
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Error deleting drafts of chain:',
        error?.message
      );
    }
  }

  public async updateCloudSyncStateByDraft(
    acukey: string,
    acid: number,
    cloudSyncState: CloudSyncState,
    chainRootAcId?: number,
  ): Promise<void> {
    try {
      return await this.dynFormDataDaoService.updateCloudSyncStateByDraft(
        acukey,
        acid,
        cloudSyncState,
        chainRootAcId,
      );
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Error updating cloudSyncState by acukey/acid:',
        error?.message
      );
    }
  }

  public async deleteFormData(dynFormData: DynFormData): Promise<boolean> {
    try {
      return await this.dynFormDataDaoService.deleteDynFormData(dynFormData);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Error Deleting Form Data:',
        error?.message
      );
      return undefined;
    }
  }

  public async createNewFormData(
    dynForm: DynForm,
    targetObjRef: IMSObjectType,
    object?: IMSObject
  ): Promise<DynFormData> {
    let obj =
      object ?? this.clientStateService.getImsObjectForForm(targetObjRef);
    if (obj === undefined) {
      throw new Error(
        this.constructor.name +
          '- Could not load Ims Object ' +
          targetObjRef +
          ' to load dynForm'
      );
    }
    return this.dynFormDataDaoService.createNewFormData(
      dynForm,
      obj,
      this.getAdditionalDynFormDataKey(
        targetObjRef,
        dynForm.form.form.$.additionalDataKey
      )
    );
  }

  public async getDynFormDataByExtFileId(
    extFileId: string,
  ): Promise<DynFormData | undefined> {
    return this.dynFormDataDaoService.getDynFormDataByExtFileId(extFileId);
  }

  public async getDynFormDataByFormIdAndReferenceTs(
    formId: number,
    referenceTs: number,
  ): Promise<DynFormData | undefined> {
    return this.dynFormDataDaoService.getDynFormDataByFormIdAndReferenceTs(formId, referenceTs);
  }

  public async clearOnlyDefinitions() {
    await this.dynFormDaoService.clear();
    this.dynForms$.next([]);
  }

  // Resetable
  public async reset() {
    await this.dynFormDaoService.reset();
    await this.dynFormDataDaoService.reset();
  }

  public async clear() {
    await this.dynFormDataDaoService.clear();
    await this.dynFormDaoService.clear();
    this.dynForms$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    // Before a sync only clear the dyn form dao service
    // keep the form data.
    await this.dynFormDaoService.clear();
  }

  public async postsync(): Promise<void> {}
  public async presync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalDynForms();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions

  private async executeForBinds(data: any, fields: AbstractFormField[]) {
    for (const field of fields) {
      if (field.bindTo) {
        // JB20200107: TODO do not like the direct comparision to "JA" but
        // thats the functionallity of the Android App. Better would be an
        // Attribute in the XML that tells which option represents true or false.
        // Than a mapping with the value against the new attribute to get the
        // boolean value.
        if (field.bindTo === '#jobfinished' && data[field.name] === 'Ja') {
          this.logger.log(this.constructor.name, '- Execute #jobfinished bind');
          await this.servicejobsService.setFinishWithNextServicejobStatusChange(
            true
          );
        }
        if (field.bindTo === '#notdelivered' && data[field.name]) {
          this.logger.log(
            this.constructor.name,
            '- Execute #notdelivered bind'
          );
          await this.servicejobsService.setNextTourJobEndNotDelivered(true);
        }
      }
    }
  }

  private async loadLocalDynForms() {
    try {
      this.logger.log(this.constructor.name, '- Load local DynForms');
      let dynForms: DynForm[] = await this.dynFormDaoService.getDynForms();

      this.dynForms$.next(dynForms);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local DynForms: ',
        error?.message
      );
      throw error;
    }
  }

  private createDynFormStateControls(formGroup: {
    [key: string]: AbstractControl;
  }): {
    [key: string]: AbstractControl;
  } {
    formGroup[DynFormStateKeys.NEEDS_SIGNATURE] = new UntypedFormControl(false);
    return formGroup;
  }

  private getAdditionalDynFormDataKey(
    targetRef: IMSObjectType,
    additionalDataKeyType: string
  ): string {
    switch (targetRef) {
      case 'ims/ServiceJob':
        return this.getAdditonalDynFormDataKeyServicejob(additionalDataKeyType);
      case 'ims/Employee':
        return this.getAdditionalDynFormDataKeyEmployee(additionalDataKeyType);
      default:
        this.logger.warn(
          this.constructor.name,
          'targetRef',
          targetRef,
          'not yet supported for additional dynformdatakey'
        );
        return undefined;
    }
  }

  private getAdditonalDynFormDataKeyServicejob(
    additionalDataKeyType: string
  ): string {
    switch (additionalDataKeyType) {
      case 'jobsection':
        let jobSectionId =
          this.clientStateService.theState.getValue().jobSectionId;
        return jobSectionId ? '_js' + jobSectionId.imsId : undefined;
      case 'deficiencyticket':
        let deficiencyTicketId =
          this.clientStateService.theState.getValue().deficiencyTicketId;
        let deficiencyTicket =
          this.deficiencyTicketService.getDeficiencyTicketById(
            deficiencyTicketId
          );
        return deficiencyTicketId
          ? '_dt' +
              deficiencyTicket.entityCreatedDeviceToken +
              '_' +
              deficiencyTicket.entityCreatedLocalDeviceId
          : undefined;
      default:
        this.logger.warn(
          this.constructor.name,
          'additionalDataKeyType',
          additionalDataKeyType,
          'not yet supported for additional dynformdatakey for servicejob'
        );
        return undefined;
    }
  }

   private getAdditionalDynFormDataKeyEmployee(
    additionalDataKeyType: string
  ): string {
    switch (additionalDataKeyType) {
      case 'tour':
        let tourId =
          this.clientStateService.theState.getValue().tourId;
        return tourId ? '_t' + tourId.imsId : undefined;
      default:
        this.logger.warn(
          this.constructor.name,
          'additionalDataKeyType',
          additionalDataKeyType,
          'not yet supported for additional dynformdatakey for employee'
        );
        return undefined;
    }
  }

  async initializeCompleted(): Promise<void> {}
}
